import { isBefore, isSameDay, startOfDay } from 'date-fns';
import React, { useRef, useState } from 'react';
import { CheckTodo, IconFormNames, Repeat } from 'shared/assets/icons';
import { HideOnTodoToggle } from 'shared/components/ui/hide-on-todo-toggle';
import { TaskTypeToggle } from 'shared/components/ui/task-type-toggle';
import { useToday } from 'shared/contexts/today';
import { UpdateTaskRepeatTimeslotDialog } from 'shared/dialogs/update-task-repeat-timeslot';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { usePlannedOccurrenceForDate } from 'shared/hooks/use-planned-occurrence-for-date';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { CreateTask, LinkOptions, Task } from 'shared/types/task';
import { TaskPriorityOptions, TaskType } from 'shared/types/task-base';
import { Timeslot, TimeslotEffectOptions } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';

import { useData } from '../../use-data';
import { MetaArchiveHabit } from '../meta-archive-habit';
import { MetaCompleteHabit } from '../meta-complete-habit';
import { MetaDeadline } from '../meta-deadline';
import { MetaDeadlineDeadline } from '../meta-deadline-deadline';
import { MetaDeleteHabit } from '../meta-delete-habit';
import { MetaDuration } from '../meta-duration';
import { MetaIcon } from '../meta-icon';
import { MetaLinkedItem } from '../meta-linked-item';
import { MetaPriority } from '../meta-priority';
import { MetaReminder } from '../meta-reminder';
import { MetaStartTime } from '../meta-start-time';
import { MetaTags } from '../meta-tags';
import * as Styled from './meta-settings.style';

export type MetaSettingsProps = {
  task: Task | CreateTask;
  updateTask: (update: Partial<Task>) => void;
  updateRelationships: (relationshipIds: ID[]) => void;
  isCreate?: boolean;
  onDelete?: () => void;
  onArchive?: () => void;
};

export const MetaSettings: React.FC<MetaSettingsProps> = ({
  task,
  updateTask,
  updateRelationships,
  isCreate,
  onDelete,
  onArchive,
}: MetaSettingsProps) => {
  const timeslotChangeRef = useRef<Timeslot>();
  const [timeslotChangeQuestionOpen, setTimeslotUpdateQuestionOpen] =
    useState(false);

  const today = useToday();
  const {
    weekStartsOn,
    dateFormat,
    createHabitAllowed,
    timeFormat,
    openPremiumDialog,
  } = useData();

  const activeSchedule = useActiveSchedule(task.schedules, today);
  const activePlannedOccurrence = usePlannedOccurrenceForDate(
    task.schedules,
    today,
  );
  const activeTimeslot =
    activePlannedOccurrence?.timeslot ??
    activeSchedule?.timeslot ??
    task.timeslot;

  const updateDeadline = (deadline?: Timestamp) =>
    updateTask({
      endStrategy: { deadline: deadline ?? null, completionCount: null },
    });

  const updateSchedule = (schedules: HabitSchedule[] | null) => {
    if (schedules?.length) {
      const activeSchedule =
        getActiveSchedule(schedules, today) ?? schedules[schedules.length - 1];
      activeSchedule.timeslot =
        activeSchedule.timeslot ?? task.timeslot ?? null;

      updateTask({
        schedules,
        timeslot: null,
        endStrategy: null,
        deadlineDeadline: null,
        type: [TaskType.Habit, TaskType.Repeating].includes(
          task.type as TaskType,
        )
          ? (task.type as TaskType.Habit | TaskType.Repeating)
          : createHabitAllowed
            ? TaskType.Habit
            : TaskType.Repeating,
      });
      return;
    }

    updateTask({
      schedules: null,
      type: TaskType.Task,
    });
  };

  const onUpdateStartTime = (startTime?: number) => {
    if (activeTimeslot?.startTime === startTime) {
      return;
    }

    if (task.type === TaskType.Task) {
      updateTask({
        timeslot: { ...(task.timeslot ?? {}), startTime: startTime ?? null },
      });
      return;
    }

    const _timeslot = {
      startTime: startTime ?? null,
      duration: activeTimeslot?.duration ?? null,
    };

    if (isCreate && activeSchedule) {
      const endedSchedules =
        task.schedules?.filter(({ endDate }) => !!endDate) ?? [];
      const updateSchedules = [];

      // the new active schedule has no planned occurrences if the timeslotEffect is set to all occurrences
      const newSchedule = {
        ...activeSchedule,
        plannedOccurrences: [],
        timeslot: _timeslot,
      };

      // create a new schedule if the activeSchedule did not start today
      if (isBefore(startOfDay(activeSchedule.startDate), startOfDay(today))) {
        const endDate = new Date();

        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule.plannedOccurrences?.filter(
            ({ date }) =>
              isBefore(date, startOfDay(endDate)) && !isSameDay(date, endDate),
          ),
        });

        // when a new schedule is created, the entered data should be reset, as these stay in the just closed schedule
        newSchedule.startDate = endDate;
        newSchedule.entries = [];
        newSchedule.completions = [];
        newSchedule.skips = [];
      }

      updateSchedules.push(newSchedule);

      // close active schedule and create a new one based on the current
      updateTask({ schedules: [...endedSchedules, ...updateSchedules] });
      return;
    }

    timeslotChangeRef.current = _timeslot;
    setTimeslotUpdateQuestionOpen(true);
  };

  const onUpdateDuration = (duration?: number) => {
    if (activeTimeslot?.duration === duration) {
      return;
    }

    if (task.type === TaskType.Task) {
      updateTask({
        timeslot: { ...(task.timeslot ?? {}), duration: duration ?? null },
      });
      return;
    }

    const _timeslot = {
      startTime: activeTimeslot?.startTime ?? null,
      duration: duration ?? null,
    };

    if (isCreate && activeSchedule) {
      const endedSchedules =
        task.schedules?.filter(({ endDate }) => !!endDate) ?? [];
      const updateSchedules = [];

      // the new active schedule has no planned occurrences if the timeslotEffect is set to all occurrences
      const newSchedule = {
        ...activeSchedule,
        plannedOccurrences: [],
        timeslot: _timeslot,
      };

      // create a new schedule if the activeSchedule did not start today
      if (isBefore(startOfDay(activeSchedule.startDate), startOfDay(today))) {
        const endDate = new Date();

        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule.plannedOccurrences?.filter(
            ({ date }) =>
              isBefore(date, startOfDay(endDate)) && !isSameDay(date, endDate),
          ),
        });

        // when a new schedule is created, the entered data should be reset, as these stay in the just closed schedule
        newSchedule.startDate = endDate;
        newSchedule.entries = [];
        newSchedule.completions = [];
        newSchedule.skips = [];
      }

      updateSchedules.push(newSchedule);

      // close active schedule and create a new one based on the current
      updateTask({ schedules: [...endedSchedules, ...updateSchedules] });
      return;
    }

    timeslotChangeRef.current = _timeslot;
    setTimeslotUpdateQuestionOpen(true);
  };

  const updateSchedulesTimeslot = (
    timeslotEffectOption: TimeslotEffectOptions,
  ) => {
    if (
      !timeslotChangeRef.current ||
      !timeslotChangeQuestionOpen ||
      !activeSchedule
    ) {
      setTimeslotUpdateQuestionOpen(false);
      timeslotChangeRef.current = undefined;
      return;
    }

    if (timeslotEffectOption === TimeslotEffectOptions.AllOccurrences) {
      const endedSchedules =
        task.schedules?.filter(({ endDate }) => !!endDate) ?? [];
      const updateSchedules = [];

      // the new active schedule has no planned occurrences if the timeslotEffect is set to all occurrences
      const newSchedule = {
        ...activeSchedule,
        plannedOccurrences: [],
        timeslot: timeslotChangeRef.current,
      };

      // create a new schedule if the activeSchedule did not start today
      if (isBefore(startOfDay(activeSchedule.startDate), startOfDay(today))) {
        const endDate = new Date();

        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule.plannedOccurrences?.filter(
            ({ date }) =>
              isBefore(date, startOfDay(endDate)) && !isSameDay(date, endDate),
          ),
        });

        // when a new schedule is created, the entered data should be reset, as these stay in the just closed schedule
        newSchedule.startDate = endDate;
        newSchedule.entries = [];
        newSchedule.completions = [];
        newSchedule.skips = [];
      }

      updateSchedules.push(newSchedule);

      // close active schedule and create a new one based on the current
      updateTask({ schedules: [...endedSchedules, ...updateSchedules] });
    }

    if (timeslotEffectOption === TimeslotEffectOptions.SingleOccurrence) {
      const schedulesClone = task.schedules
        ? createPlannedOccurrenceForDate(task.schedules, today, {
            timeslot: timeslotChangeRef.current,
            weekStartsOn,
            today,
            id: activePlannedOccurrence?.id,
          })
        : undefined;

      updateTask({ schedules: schedulesClone });
    }

    setTimeslotUpdateQuestionOpen(false);
    timeslotChangeRef.current = undefined;
  };

  const updateDeadlineDeadline = (deadlineDeadline?: Timestamp) =>
    updateTask({
      deadlineDeadline: deadlineDeadline ?? null,
    });

  const updatePriority = (priority: TaskPriorityOptions | null) =>
    updateTask({ priority });

  const updateMetaTags = (metaTagIds: ID[]) => updateRelationships(metaTagIds);

  const updateLinkedItem = (link: { id: ID; type: LinkOptions } | null) =>
    updateTask({
      lifeAreaId: link?.type === LinkOptions.LifeArea ? link.id : null,
      goalId: link?.type === LinkOptions.Goal ? link.id : null,
    });

  const updateReminderTime = (reminderTime?: number) =>
    updateTask({ reminderTime: reminderTime ?? null });

  const updateIconName = (iconName: IconFormNames) => updateTask({ iconName });

  const updateType = (type: TaskType) => {
    if (type === TaskType.Habit && !createHabitAllowed) {
      openPremiumDialog();
      return;
    }

    updateTask({ type });
  };

  const updateHideOnTodo = (hideOnTodo: boolean) =>
    updateTask({ hideOnTodo, type: task.type as TaskType.Habit });

  const updateCompletedAt = (completedAt: Timestamp | null) =>
    updateTask({ completedAt });

  return (
    <>
      <Styled.List>
        <Styled.ListItem>
          <MetaLinkedItem
            onChange={updateLinkedItem}
            value={task.goalId ?? task.lifeAreaId}
            isCreate={isCreate}
          />
        </Styled.ListItem>

        <Styled.ListItem>
          <MetaDeadline
            name={task.name ?? ''}
            onChangeDeadline={updateDeadline}
            onChangeSchedule={updateSchedule}
            dateFormat={dateFormat}
            weekStartsOn={weekStartsOn}
            timeFormat={timeFormat}
            deadline={task.endStrategy?.deadline}
            schedules={task.schedules}
            type={task.type}
          />
        </Styled.ListItem>

        {task.type === TaskType.Task && (
          <>
            <Styled.ListItem>
              <MetaStartTime
                timeFormat={timeFormat}
                onChange={onUpdateStartTime}
                value={activeTimeslot?.startTime}
              />
            </Styled.ListItem>

            <Styled.ListItem>
              <MetaDuration
                onChange={onUpdateDuration}
                value={activeTimeslot?.duration}
              />
            </Styled.ListItem>

            <Styled.ListItem>
              <MetaDeadlineDeadline
                onChange={updateDeadlineDeadline}
                dateFormat={dateFormat}
                weekStartsOn={weekStartsOn}
                value={task.deadlineDeadline ?? undefined}
              />
            </Styled.ListItem>
          </>
        )}

        <Styled.ListItem>
          <MetaPriority
            onChange={updatePriority}
            value={
              !task.priority || task.priority === TaskPriorityOptions.None
                ? null
                : task.priority
            }
          />
        </Styled.ListItem>

        <Styled.ListItem>
          <MetaTags
            taskId={(task as Task).id}
            tagIds={(task as CreateTask).tagIds ?? undefined}
            onChange={updateMetaTags}
          />
        </Styled.ListItem>

        <Styled.ListItem>
          <MetaReminder
            timeFormat={timeFormat}
            onChange={updateReminderTime}
            value={task.reminderTime}
          />
        </Styled.ListItem>

        {[TaskType.Habit, TaskType.Repeating].includes(task.type) && (
          <Styled.ListItem>
            <MetaIcon
              value={task.iconName ?? IconFormNames.Repeat}
              onChange={updateIconName}
            />
          </Styled.ListItem>
        )}
      </Styled.List>
      {[TaskType.Habit, TaskType.Repeating].includes(task.type) && (
        <>
          <Styled.Divider />
          <Styled.List $invertVerticalPadding>
            <Styled.ListItem $horizontalPadding $verticalPadding>
              <TaskTypeToggle
                type={task.type as TaskType.Repeating | TaskType.Habit}
                onChange={updateType}
                fullWidth
                icon={Repeat}
              />
            </Styled.ListItem>
            <Styled.ListItem $horizontalPadding $verticalPadding>
              <HideOnTodoToggle
                value={!!task.hideOnTodo}
                onChange={updateHideOnTodo}
                disabled={task.type !== TaskType.Habit}
                fullWidth
                icon={CheckTodo}
              />
            </Styled.ListItem>
          </Styled.List>
        </>
      )}
      {[TaskType.Habit, TaskType.Repeating].includes(task.type) && (
        <>
          <Styled.Divider />
          <Styled.List $invertVerticalPadding>
            <Styled.ListItem>
              <MetaCompleteHabit
                task={task as Task}
                onUpdate={updateCompletedAt}
              />
            </Styled.ListItem>
          </Styled.List>
          <Styled.Divider />
          <Styled.List $invertVerticalPadding>
            {onArchive && (
              <Styled.ListItem>
                <MetaArchiveHabit onArchive={onArchive} />
              </Styled.ListItem>
            )}

            {onDelete && (
              <Styled.ListItem>
                <MetaDeleteHabit onDelete={onDelete} />
              </Styled.ListItem>
            )}
          </Styled.List>
        </>
      )}

      <UpdateTaskRepeatTimeslotDialog
        open={timeslotChangeQuestionOpen}
        onSubmit={updateSchedulesTimeslot}
      />
    </>
  );
};
